import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp, deleteDoc, onSnapshot } from 'firebase/firestore';
import { toast } from 'sonner';
import { db } from '../../firebase-config';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Users, Mail, Clock, XCircle, Trash2, Send, X, Loader2 } from 'lucide-react';

interface Admin {
  id: string;
  email: string;
  name: string;
  role: string;
  createdAt: any;
}

interface Invitation {
  id: string;
  email: string;
  invitedBy: string;
  invitedByName: string;
  status: string;
  createdAt: any;
  expiresAt: any;
}

interface TeamManagementProps {
  customerId: string;
  brandColor: string;
}

export default function TeamManagement({ customerId, brandColor }: TeamManagementProps) {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [currentAdminName, setCurrentAdminName] = useState('');

  useEffect(() => {
    fetchCurrentAdmin();
  }, [customerId, user]);

  // Real-time listeners for team data
  useEffect(() => {
    if (!customerId) return;

    console.log('🔄 Setting up real-time listeners for customerId:', customerId);
    setLoading(true);

    // Listen to admins collection
    const unsubscribeAdmins = onSnapshot(
      collection(db, 'customers', customerId, 'admins'),
      (snapshot) => {
        const adminsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Admin[];
        console.log('👥 Admins updated:', adminsData.length, 'admins', adminsData);
        setAdmins(adminsData);
        setLoading(false);
      },
      (error) => {
        console.error('❌ Error fetching admins:', error);
        setLoading(false);
      }
    );

    // Listen to pending invitations
    const unsubscribeInvitations = onSnapshot(
      query(
        collection(db, 'customers', customerId, 'invitations'),
        where('status', '==', 'pending')
      ),
      (snapshot) => {
        const invitationsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Invitation[];
        console.log('📧 Pending invitations updated:', invitationsData.length, 'invitations', invitationsData);
        setInvitations(invitationsData);
      },
      (error) => {
        console.error('❌ Error fetching invitations:', error);
      }
    );

    // Cleanup listeners on unmount
    return () => {
      console.log('🧹 Cleaning up real-time listeners');
      unsubscribeAdmins();
      unsubscribeInvitations();
    };
  }, [customerId]);

  const fetchCurrentAdmin = async () => {
    if (!user) return;

    try {
      // Check if user is super admin first
      const superAdminDoc = await getDocs(query(collection(db, 'admins'), where('__name__', '==', user.uid)));
      if (!superAdminDoc.empty) {
        const adminData = superAdminDoc.docs[0].data();
        setCurrentAdminName(adminData.name || user.email || 'Admin');
        return;
      }

      // Check if user is customer admin
      const customerAdminDoc = await getDocs(
        query(collection(db, 'customers', customerId, 'admins'), where('__name__', '==', user.uid))
      );
      if (!customerAdminDoc.empty) {
        const adminData = customerAdminDoc.docs[0].data();
        setCurrentAdminName(adminData.name || user.email || 'Admin');
      }
    } catch (error) {
      console.error('Error fetching current admin:', error);
    }
  };

  const handleSendInvitation = async () => {
    if (!inviteEmail || !user) return;

    setSending(true);
    try {
      // Check if email is already an admin
      const existingAdmin = admins.find(admin => admin.email.toLowerCase() === inviteEmail.toLowerCase());
      if (existingAdmin) {
        toast.error('This email is already an admin.');
        setSending(false);
        return;
      }

      // Check if there's already a pending invitation
      const existingInvitation = invitations.find(
        inv => inv.email.toLowerCase() === inviteEmail.toLowerCase() && inv.status === 'pending'
      );
      if (existingInvitation) {
        toast.error('There is already a pending invitation for this email.');
        setSending(false);
        return;
      }

      // Create invitation document
      const invitationData = {
        customerId,
        email: inviteEmail.toLowerCase(),
        invitedBy: user.uid,
        invitedByName: currentAdminName,
        role: 'customer_admin',
        status: 'pending',
        createdAt: Timestamp.now(),
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days
      };

      await addDoc(collection(db, 'customers', customerId, 'invitations'), invitationData);

      // Real-time listener will automatically update the UI
      setShowInviteModal(false);
      setInviteEmail('');
      toast.success('Invitation sent successfully!');
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    if (!confirm('Are you sure you want to revoke this invitation?')) return;

    try {
      await updateDoc(doc(db, 'customers', customerId, 'invitations', invitationId), {
        status: 'revoked',
      });
      // Real-time listener will automatically update the UI
    } catch (error) {
      console.error('Error revoking invitation:', error);
      toast.error('Failed to revoke invitation. Please try again.');
    }
  };

  const handleRemoveAdmin = async (adminId: string, adminEmail: string) => {
    if (!confirm(`Are you sure you want to remove ${adminEmail} from the team?`)) return;

    try {
      await deleteDoc(doc(db, 'customers', customerId, 'admins', adminId));
      // Real-time listener will automatically update the UI
      toast.success('Admin removed successfully.');
    } catch (error) {
      console.error('Error removing admin:', error);
      toast.error('Failed to remove admin. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Team Management</h2>
          <p className="text-sm text-muted-foreground">Manage your admin team and invitations</p>
        </div>
        <Button onClick={() => setShowInviteModal(true)} style={{ backgroundColor: brandColor }} className="text-white">
          <Mail className="h-4 w-4 mr-2" />
          Invite Admin
        </Button>
      </div>

      {/* Current Admins */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Team Members ({admins.length})</h3>
        </div>
        <div className="space-y-3">
          {admins.length === 0 ? (
            <p className="text-sm text-muted-foreground">No team members yet.</p>
          ) : (
            admins.map(admin => (
              <div
                key={admin.id}
                className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{admin.name}</p>
                    <p className="text-sm text-muted-foreground">{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">Active</span>
                  {admin.id !== user?.uid && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAdmin(admin.id, admin.email)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold text-foreground">Pending Invitations ({invitations.length})</h3>
          </div>
          <div className="space-y-3">
            {invitations.map(invitation => {
              const isExpired = invitation.expiresAt.toDate() < new Date();
              return (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{invitation.email}</p>
                      <p className="text-xs text-muted-foreground">
                        Invited by {invitation.invitedByName} on{' '}
                        {invitation.createdAt.toDate().toLocaleDateString()}
                      </p>
                      {isExpired && (
                        <p className="text-xs text-destructive mt-1">Expired</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                      {isExpired ? 'Expired' : 'Pending'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevokeInvitation(invitation.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Invite Team Member</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowInviteModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="teammate@example.com"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  They'll receive an invitation email with a link to join your team.
                </p>
              </div>

              <div className="bg-muted/50 border border-border rounded p-3">
                <p className="text-xs text-muted-foreground">
                  <strong>Note:</strong> The invitation will expire in 7 days. They'll be able to access the
                  admin portal and manage feature requests.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSendInvitation}
                  disabled={!inviteEmail || sending}
                  style={{ backgroundColor: brandColor }}
                  className="text-white flex-1"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
